import { useState } from 'react';
import { fmt, fmtRatePct } from '../../lib/nmtc/calculations';

const BUSINESS_TYPES = [
  '',
  'Manufacturing',
  'Healthcare / Community Clinic',
  'Charter School / Education',
  'Mixed-Use / Commercial Real Estate',
  'Non-Profit Service Organization',
  'Child Care / Early Education',
  'Grocery / Food Access',
  'Technology / Office',
  'Arts / Cultural Facility',
  'Other',
];

const LOAN_TERMS = [10, 15, 20, 25, 30];

const DEBT_TYPE_LABELS = {
  bank:  'Bank Financing',
  cdfi:  'CDFI',
  other: 'Other',
};

function CurrencyInput({ label, value, onChange, hint, optional }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
        {label}
        {optional && <span className="ml-1.5 text-xs font-normal text-gray-400">(optional)</span>}
      </label>
      {hint && <p className="text-xs text-gray-400 mb-1.5">{hint}</p>}
      <div className="flex items-center gap-2">
        <span className="text-gray-400 font-medium text-sm">$</span>
        <input
          type="number"
          min={0}
          step={100000}
          value={value || ''}
          placeholder="0"
          onChange={(e) => onChange(Number(e.target.value))}
          className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-400 text-base"
        />
      </div>
    </div>
  );
}

export default function ScenarioPanel({ inputs, calc, onChange }) {
  const [eligibilityOpen, setEligibilityOpen] = useState(false);
  const [projectCostsOpen, setProjectCostsOpen] = useState(false);

  function set(key, value) {
    onChange({ ...inputs, [key]: value });
  }

  const tooSmall = calc.qei > 0 && calc.qei < 3000000;
  const smallDeal = calc.qei >= 3000000 && calc.qei < 5000000;

  return (
    <div className="rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-100">

        {/* ── LEFT: Inputs ── */}
        <div className="bg-white p-6 space-y-7">

          {/* Section 0: Project Size */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-full bg-slate-700 text-white text-xs font-bold flex items-center justify-center">★</div>
              <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide">Project Size</h3>
              <span className="text-xs font-normal text-gray-400">total QEI</span>
            </div>

            <div className="flex flex-wrap gap-2">
              {[5000000, 7500000, 10000000, 15000000, 20000000].map((size) => (
                <button
                  key={size}
                  onClick={() => onChange({ ...inputs, qeiDirect: inputs.qeiDirect === size ? undefined : size })}
                  className={`rounded-lg px-3 py-2 text-xs font-semibold transition ${
                    inputs.qeiDirect === size
                      ? 'bg-emerald-900 text-white'
                      : 'bg-white text-gray-700 border border-gray-200 hover:bg-emerald-50'
                  }`}
                >
                  ${size >= 1000000 ? `${size / 1000000}M` : size}
                </button>
              ))}
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Custom deal size</label>
              <div className="flex items-center gap-2">
                <span className="text-gray-400 font-medium text-sm">$</span>
                <input
                  type="number"
                  min={0}
                  step={500000}
                  value={inputs.qeiDirect || ''}
                  placeholder="Enter total QEI..."
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    onChange({ ...inputs, qeiDirect: v > 0 ? v : undefined });
                  }}
                  className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-400 text-sm"
                />
                {inputs.qeiDirect && (
                  <button
                    onClick={() => onChange({ ...inputs, qeiDirect: undefined })}
                    className="text-xs text-gray-400 hover:text-red-500 transition px-2 py-1 rounded border border-gray-200"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            {inputs.qeiDirect ? (
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                <span className="text-emerald-700">Leverage Loan</span>
                <span className="font-semibold text-emerald-900 text-right">{fmt(calc.leverageLoan)}</span>
                <span className="text-emerald-700">NMTC Equity</span>
                <span className="font-bold text-emerald-900 text-right">{fmt(calc.nmtcEquity)}</span>
                <span className="text-xs text-emerald-600 col-span-2">Enter your owner equity below → hard debt is derived</span>
              </div>
            ) : (
              <p className="text-xs text-gray-400">Select a preset size or enter a custom QEI. Leave blank to back-solve from equity + hard debt below.</p>
            )}
          </div>

          {/* Section 1: Tax Credit Pricing */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-full bg-emerald-900 text-white text-xs font-bold flex items-center justify-center">1</div>
              <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide">Tax Credit Pricing</h3>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-semibold text-gray-700">Credit Price (per $1.00 of credit)</label>
                <span className="text-base font-bold text-emerald-700">${inputs.taxCreditPrice.toFixed(2)}</span>
              </div>
              <input
                type="range"
                min={0.65}
                max={0.95}
                step={0.01}
                value={inputs.taxCreditPrice}
                onChange={(e) => set('taxCreditPrice', Number(e.target.value))}
                className="w-full h-1.5 rounded-full accent-emerald-700"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-0.5">
                <span>$0.65</span>
                <span>$0.95</span>
              </div>
              <p className="text-xs text-gray-400 mt-1.5">
                Market typically ranges $0.75–$0.92. Higher price = more NMTC equity raised.
              </p>
            </div>

            <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <span className="text-emerald-700">Tax Credit Rate</span>
              <span className="font-semibold text-emerald-900 text-right">39% of QEI</span>
              <span className="text-emerald-700">NMTC Equity Raised</span>
              <span className="font-bold text-emerald-900 text-right">{fmt(calc.nmtcEquity)}</span>
              <span className="text-emerald-700">Total Credits Generated</span>
              <span className="font-semibold text-emerald-900 text-right">{fmt(calc.totalTaxCredits)}</span>
            </div>
          </div>

          {/* Section 2: Project Capital */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-full bg-emerald-900 text-white text-xs font-bold flex items-center justify-center">2</div>
              <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide">Project Capital</h3>
            </div>

            <CurrencyInput
              label="Owner's Equity Available"
              value={inputs.ownerEquity}
              onChange={(v) => set('ownerEquity', v)}
              hint="Your cash or equity contribution going into the deal"
            />

            {inputs.qeiDirect ? (
              <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm">
                <div className="flex justify-between">
                  <div>
                    <p className="font-semibold text-amber-800">Hard Debt (derived)</p>
                    <p className="text-xs text-amber-600 mt-0.5">= Leverage Loan − Owner Equity</p>
                  </div>
                  <span className="font-bold text-amber-900 text-base">{fmt(calc.hardDebt)}</span>
                </div>
              </div>
            ) : (
              <CurrencyInput
                label="Hard Debt"
                value={inputs.hardDebt}
                onChange={(v) => set('hardDebt', v)}
                hint="Bank loan, CDFI loan, or other conventional financing"
              />
            )}

            <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>Leverage Loan Total</span>
                <span className="font-bold text-gray-900">{fmt(calc.leverageLoan)}</span>
              </div>
              <p className="text-xs text-gray-400 mt-1">Owner equity + hard debt flowing into the Investment Fund</p>
            </div>

            {tooSmall && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                <strong>Heads up:</strong> QEI under $3M is rarely cost-effective — fixed closing costs consume too much of the benefit.
              </div>
            )}
            {smallDeal && (
              <div className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-800">
                <strong>Note:</strong> $3M–$5M deals are feasible but tight on fees. Confirm CDE appetite before proceeding.
              </div>
            )}
          </div>

          {/* Section 3: Financing Terms */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-full bg-emerald-900 text-white text-xs font-bold flex items-center justify-center">3</div>
              <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide">Financing Terms</h3>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Hard Debt Type</label>
              <div className="flex gap-2">
                {['bank', 'cdfi', 'other'].map((type) => (
                  <button
                    key={type}
                    onClick={() => set('hardDebtType', type)}
                    className={`flex-1 rounded-lg px-2 py-2 text-xs font-medium transition ${
                      inputs.hardDebtType === type
                        ? 'bg-emerald-900 text-white'
                        : 'bg-white text-gray-700 border border-gray-200 hover:bg-emerald-50'
                    }`}
                  >
                    {DEBT_TYPE_LABELS[type]}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-semibold text-gray-700">Interest Rate</label>
                <span className="text-base font-bold text-emerald-700">{inputs.hardDebtRate.toFixed(2)}%</span>
              </div>
              <input
                type="range"
                min={3.0}
                max={12.0}
                step={0.25}
                value={inputs.hardDebtRate}
                onChange={(e) => set('hardDebtRate', Number(e.target.value))}
                className="w-full h-1.5 rounded-full accent-emerald-700"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-0.5">
                <span>3.00%</span>
                <span>12.00%</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Loan Term</label>
              <div className="flex gap-2">
                {LOAN_TERMS.map((yr) => (
                  <button
                    key={yr}
                    onClick={() => set('loanTerm', yr)}
                    className={`flex-1 rounded-lg py-2 text-xs font-medium transition ${
                      inputs.loanTerm === yr
                        ? 'bg-emerald-900 text-white'
                        : 'bg-white text-gray-700 border border-gray-200 hover:bg-emerald-50'
                    }`}
                  >
                    {yr}yr
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-semibold text-gray-700">Sub-Allocation Fee</label>
                <span className="text-base font-bold text-emerald-700">{inputs.subAllocationPct.toFixed(1)}%</span>
              </div>
              <input
                type="range"
                min={3.0}
                max={5.0}
                step={0.25}
                value={inputs.subAllocationPct}
                onChange={(e) => set('subAllocationPct', Number(e.target.value))}
                className="w-full h-1.5 rounded-full accent-emerald-700"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-0.5">
                <span>3.0%</span>
                <span>5.0%</span>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Paid to the CDE at closing for NMTC allocation. Typical range: 3–5% of QEI.
              </p>
            </div>
          </div>

          {/* Section 4: Project Costs */}
          <div>
            <button
              onClick={() => setProjectCostsOpen((o) => !o)}
              className="flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-gray-700 transition w-full text-left"
            >
              <div className="w-5 h-5 rounded-full bg-gray-300 text-white text-xs font-bold flex items-center justify-center">4</div>
              <span>Project Costs</span>
              <span className="text-xs font-normal text-gray-400 ml-1">for Sources &amp; Uses</span>
              <span className={`ml-auto text-xs transition-transform duration-200 ${projectCostsOpen ? 'rotate-180' : ''}`}>▼</span>
            </button>

            {projectCostsOpen && (
              <div className="mt-4 space-y-4 pl-7">
                <CurrencyInput
                  label="Hard Costs"
                  value={inputs.hardCosts ?? 0}
                  onChange={(v) => set('hardCosts', v || undefined)}
                  hint="Construction, acquisition, and other hard project costs"
                  optional
                />
                <CurrencyInput
                  label="Soft Costs"
                  value={inputs.softCosts ?? 0}
                  onChange={(v) => set('softCosts', v || undefined)}
                  hint="Architecture, engineering, permits, financing costs, etc."
                  optional
                />
                {(inputs.hardCosts || inputs.softCosts) ? (
                  <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2.5 text-sm">
                    <div className="flex justify-between text-gray-600">
                      <span>Total Project Cost</span>
                      <span className="font-bold text-gray-900">{fmt((inputs.hardCosts ?? 0) + (inputs.softCosts ?? 0))}</span>
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </div>

          {/* Section 5: Eligibility */}
          <div>
            <button
              onClick={() => setEligibilityOpen((o) => !o)}
              className="flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-gray-700 transition w-full text-left"
            >
              <div className="w-5 h-5 rounded-full bg-gray-300 text-white text-xs font-bold flex items-center justify-center">5</div>
              <span>Eligibility &amp; Project Type</span>
              <span className={`ml-auto text-xs transition-transform duration-200 ${eligibilityOpen ? 'rotate-180' : ''}`}>▼</span>
            </button>

            {eligibilityOpen && (
              <div className="mt-4 space-y-4 pl-7">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Project Location Qualifies?</label>
                  <div className="flex gap-2">
                    {[true, false].map((val) => (
                      <button
                        key={String(val)}
                        onClick={() => set('locationQualifies', val)}
                        className={`flex-1 rounded-lg px-3 py-2 text-sm font-medium transition ${
                          inputs.locationQualifies === val
                            ? 'bg-emerald-900 text-white'
                            : 'bg-white text-gray-700 border border-gray-200 hover:bg-emerald-50'
                        }`}
                      >
                        {val ? 'Yes, it qualifies' : 'Not sure / No'}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Business / Project Type</label>
                  <select
                    value={inputs.businessType}
                    onChange={(e) => set('businessType', e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-400 text-sm"
                  >
                    {BUSINESS_TYPES.map((t) => (
                      <option key={t} value={t}>{t || '— Select type —'}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Entity Type</label>
                  <div className="flex gap-2">
                    {[true, false].map((val) => (
                      <button
                        key={String(val)}
                        onClick={() => set('isNonProfit', val)}
                        className={`flex-1 rounded-lg px-3 py-2 text-sm font-medium transition ${
                          inputs.isNonProfit === val
                            ? 'bg-emerald-900 text-white'
                            : 'bg-white text-gray-700 border border-gray-200 hover:bg-emerald-50'
                        }`}
                      >
                        {val ? 'Non-Profit' : 'For-Profit'}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── RIGHT: Live Summary ── */}
        <div className="bg-gradient-to-br from-emerald-900 to-emerald-700 text-white p-6 space-y-5">

          <div>
            <p className="text-xs uppercase tracking-widest text-white/60 font-medium">
              {inputs.qeiDirect ? 'Total QEI (Direct)' : 'Total QEI (Back-Solved)'}
            </p>
            <p className="text-4xl font-bold leading-tight mt-1">{fmt(calc.qei)}</p>
            <p className="text-xs text-white/50 mt-1">
              {inputs.qeiDirect
                ? `Leverage: ${fmt(calc.leverageLoan)} · NMTC Equity: ${fmt(calc.nmtcEquity)}`
                : `= ${fmt(calc.leverageLoan)} leverage ÷ (1 − 39% × $${inputs.taxCreditPrice.toFixed(2)})`
              }
            </p>
          </div>

          <div>
            <p className="text-xs uppercase tracking-widest text-white/60 font-medium mb-2">Capital Stack</p>
            <div className="space-y-2">
              <div className="bg-white/10 rounded-lg px-3 py-2.5 flex justify-between items-center">
                <div>
                  <p className="text-xs text-white/60">Owner's Equity</p>
                  <p className="text-sm font-bold">{fmt(calc.ownerEquity)}</p>
                </div>
                <span className="text-xs font-semibold text-white/70 bg-white/10 px-2 py-0.5 rounded-full">
                  {calc.ownerEquityPct.toFixed(1)}%
                </span>
              </div>
              <div className="bg-white/10 rounded-lg px-3 py-2.5 flex justify-between items-center">
                <div>
                  <p className="text-xs text-white/60">{DEBT_TYPE_LABELS[inputs.hardDebtType || 'bank']} (Hard Debt)</p>
                  <p className="text-sm font-bold">{fmt(calc.hardDebt)}</p>
                </div>
                <span className="text-xs font-semibold text-white/70 bg-white/10 px-2 py-0.5 rounded-full">
                  {calc.hardDebtPct.toFixed(1)}%
                </span>
              </div>
              <div className="bg-white/20 rounded-lg px-3 py-2.5 flex justify-between items-center border border-white/20">
                <div>
                  <p className="text-xs text-white/70">NMTC Equity (B-Note)</p>
                  <p className="text-sm font-bold">{fmt(calc.nmtcEquity)}</p>
                  <p className="text-xs text-white/50">1.0% I/O · forgiven at Year 7</p>
                </div>
                <span className="text-xs font-semibold bg-emerald-400/30 text-emerald-100 px-2 py-0.5 rounded-full">
                  {calc.equityPct.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white/10 rounded-xl p-4 space-y-2.5">
            <p className="text-xs uppercase tracking-widest text-white/60 font-medium">Annual Costs</p>

            <div className="space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-white/70">A-Note I/O ({inputs.hardDebtRate.toFixed(2)}%)</span>
                <span className="font-medium">{fmt(calc.annualANote)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white/70">B-Note I/O (1.00%)</span>
                <span className="font-medium">{fmt(calc.annualBNote)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white/70">Asset Mgmt Fee (0.5%/yr)</span>
                <span className="font-medium">{fmt(calc.assetMgmtFeeAnnual)}</span>
              </div>
              <div className="border-t border-white/20 pt-2 flex justify-between font-bold">
                <span>Total Annual Cost</span>
                <span className="text-lg">{fmt(calc.totalAnnualNMTCService + calc.assetMgmtFeeAnnual)}</span>
              </div>
            </div>

            <div className="border-t border-white/10 pt-2.5 space-y-1">
              <p className="text-xs text-white/60 uppercase tracking-wide">Closing Fees (Year 0)</p>
              <div className="flex justify-between text-sm">
                <span className="text-white/70">Total Closing Fees</span>
                <span>{fmt(calc.totalClosingFees)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white/70">Net Proceeds to Project</span>
                <span>{fmt(calc.netProceedsToProject)}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white/10 rounded-lg p-3">
              <p className="text-xs text-white/60">Effective Rate</p>
              <p className="text-xl font-bold mt-0.5">{fmtRatePct(calc.effectiveRate)}</p>
              <p className="text-xs text-white/50 mt-0.5">blended I/O on QEI</p>
            </div>
            <div className="bg-white/10 rounded-lg p-3">
              <p className="text-xs text-white/60">B-Note (NPV)</p>
              <p className="text-xl font-bold mt-0.5">{fmt(calc.npvBNoteForgiveness)}</p>
              <p className="text-xs text-white/50 mt-0.5">forgiven at Year 7</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
